import React, { useState, useEffect } from 'react';
import { Star, Heart, Briefcase, Activity, Sparkles, ArrowLeft, Sun, Moon, Calendar, AlertTriangle, Flame, Clock, ShieldAlert, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import RelatedTools from '../components/RelatedTools';
import RashifalSubscribe from '../components/RashifalSubscribe';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import SEO from '../components/SEO';
import VaidikUpayCard from '../components/VaidikUpayCard';
import { useLang } from '../context/LanguageContext';
import { trackConsultationCTAClick, trackWhatsAppClick, trackRashifalView, initScrollDepthTracking, resetScrollDepth } from '../utils/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

const SUN_SIGN_RANGES = [
  { start: [3, 21], end: [4, 19] },  // Aries
  { start: [4, 20], end: [5, 20] },  // Taurus
  { start: [5, 21], end: [6, 20] },  // Gemini
  { start: [6, 21], end: [7, 22] },  // Cancer
  { start: [7, 23], end: [8, 22] },  // Leo
  { start: [8, 23], end: [9, 22] },  // Virgo
  { start: [9, 23], end: [10, 22] }, // Libra
  { start: [10, 23], end: [11, 21] },// Scorpio
  { start: [11, 22], end: [12, 21] },// Sagittarius
  { start: [12, 22], end: [1, 19] }, // Capricorn
  { start: [1, 20], end: [2, 18] },  // Aquarius
  { start: [2, 19], end: [3, 20] },  // Pisces
];

function getSunSignFromDOB(month, day) {
  for (let i = 0; i < SUN_SIGN_RANGES.length; i++) {
    const { start, end } = SUN_SIGN_RANGES[i];
    if (start[0] <= end[0]) {
      if ((month === start[0] && day >= start[1]) || (month === end[0] && day <= end[1]) || (month > start[0] && month < end[0])) return i;
    } else {
      if ((month === start[0] && day >= start[1]) || (month === end[0] && day <= end[1]) || month > start[0] || month < end[0]) return i;
    }
  }
  return 0;
}

export default function DailyRashifalPage() {
  const [rashifal, setRashifal] = useState([]);
  const [date, setDate] = useState('');
  const [dateHi, setDateHi] = useState('');
  const [transitSummary, setTransitSummary] = useState('');
  const [parashariSummary, setParashariSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState('sun'); // 'sun' or 'moon'
  const [dob, setDob] = useState('');
  const { lang } = useLang();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchRashifal();
    resetScrollDepth();
    initScrollDepthTracking();
  }, []);

  // (legacy document.title block removed — Helmet/SEO component handles meta now)

  const fetchRashifal = async () => {
    try {
      const res = await axios.get(`${API}/api/daily-rashifal`);
      setRashifal(res.data.rashifal || []);
      setDate(res.data.date || '');
      setDateHi(res.data.date_hi || '');
      setTransitSummary(res.data.transit_summary_hi || '');
      setParashariSummary(res.data.parashari_summary || null);
    } catch (err) {
      console.error('Rashifal fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Build a smart, pre-filled WhatsApp message that converts (serious-buyer tone)
  const buildWhatsAppLink = (rashi, isCritical) => {
    const phone = '919311369729';
    const name = rashi?.name || '';
    const greeting = lang === 'hi'
      ? `नमस्कार आचार्य Vivek जी 🙏\n\nमैं *${name}* राशि का/की हूं। आज (${dateHi || date}) का राशिफल पढ़ा।${isCritical ? ' मेरी राशि पर आज विशेष ग्रह योग है।' : ''}\n\nमुझे अपनी जन्म कुंडली का *गहन Parashari विश्लेषण और व्यक्तिगत परामर्श* करवाना है। कृपया available timing व consultation details बताएं।\n\nधन्यवाद।`
      : `Namaste Acharya Vivek ji 🙏\n\nI am a *${rashi?.name_en || name}*.${isCritical ? ' My rashi has a special transit today.' : ''}\n\nI would like to get a *detailed Parashari analysis and personal consultation* of my birth chart. Please share available timings and consultation details.\n\nThank you.`;
    return `https://wa.me/${phone}?text=${encodeURIComponent(greeting)}`;
  };

  const handleDobChange = (val) => {
    setDob(val);
    if (val) {
      const d = new Date(val);
      const idx = getSunSignFromDOB(d.getMonth() + 1, d.getDate());
      setSelected(idx);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString(lang === 'hi' ? 'hi-IN' : 'en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const ratingStars = (r) => {
    const stars = [];
    for (let i = 0; i < 5; i++) stars.push(
      <Star key={i} className={`w-3.5 h-3.5 ${i < Math.round(r/2) ? 'text-gold fill-gold' : 'text-white/20'}`} />
    );
    return stars;
  };

  const sel = selected !== null ? rashifal[selected] : null;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Daily Rashifal | आज का राशिफल | Today's Horoscope"
        breadcrumbs={[
          { name: 'होम', path: '/' },
          { name: 'राशिफल', path: '/daily-rashifal' },
        ]}
        description="आज का राशिफल — सभी 12 राशियों का विस्तृत Daily Horoscope। प्रेम, करियर, धन, स्वास्थ्य पर सटीक भविष्यवाणी, lucky numbers, colors and Vedic upay. Daily Rashifal in Hindi & English by Vivek Mudgal."
        path="/daily-rashifal"
        image="/brand/og/va-og-rashifal.png"
        keywords="daily rashifal, आज का राशिफल, today horoscope, daily horoscope, aaj ka rashifal, mesh, vrishabh, mithun, kark, simha, kanya, tula, vrishchik, dhanu, makar, kumbh, meen"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'Daily Rashifal — Today\'s Horoscope for all 12 Zodiac Signs',
          description: 'Free daily Vedic horoscope for all 12 rashis — Parashari predictions on love, career, money, health with authentic shloka citations.',
          image: 'https://viratastro.cloud/brand/va-cosmic-1024.png',
          author: {
            '@type': 'Person',
            name: 'Acharya Vivek Mudgal',
            url: 'https://viratastro.cloud/experts/vivek-virat-mudgal',
          },
          publisher: {
            '@type': 'Organization',
            name: 'ViratAstro',
            logo: {
              '@type': 'ImageObject',
              url: 'https://viratastro.cloud/brand/va-cosmic-1024.png',
            },
          },
          datePublished: (date || new Date().toISOString().slice(0, 10)),
          dateModified: (date || new Date().toISOString().slice(0, 10)),
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': 'https://viratastro.cloud/daily-rashifal',
          },
          inLanguage: ['hi-IN', 'en-IN'],
          articleSection: 'Astrology',
        }}
      />
      <Header />
      <FloatingActions />

      {/* Hero */}
      <section className="pt-32 pb-8 cosmic-bg">
        <div className="container-custom text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-primary hover:gap-3 transition-all mb-6 text-sm" data-testid="back-home-btn">
            <ArrowLeft className="w-4 h-4" /> {lang === 'hi' ? 'होम' : 'Home'}
          </Link>
          <span className="block text-xs tracking-[0.3em] uppercase text-gold font-semibold mb-4">
            {lang === 'hi' ? 'पराशरीय दैनिक राशिफल' : 'PARASHARI DAILY HOROSCOPE'}
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-text-main mb-3">
            {lang === 'hi' ? 'आज का राशिफल' : "Today's Horoscope"}
          </h1>
          <p className="text-lg text-gold mb-2 font-medium" data-testid="rashifal-date">
            {dateHi || formatDate(date)}
          </p>
          <p className="text-text-muted max-w-2xl mx-auto mb-6">
            {lang === 'hi'
              ? 'आचार्य Vivek Mudgal द्वारा — पराशर ऋषि के सिद्धांतों पर आधारित विस्तृत वैदिक भविष्यफल'
              : 'By Acharya Vivek Mudgal — Detailed Vedic predictions based on Maharshi Parashar principles'}
          </p>

          {/* Today's Sky — real planetary transit summary */}
          {transitSummary && (
            <div className="max-w-4xl mx-auto mb-6 px-4" data-testid="todays-sky">
              <div className="bg-gradient-to-r from-purple-900/30 via-gold/10 to-purple-900/30 border border-gold/30 rounded-2xl p-4 sm:p-5 backdrop-blur-sm">
                <div className="flex items-center justify-center gap-2 text-gold text-xs sm:text-sm font-semibold mb-2">
                  <Sparkles className="w-4 h-4" />
                  <span className="tracking-widest">{lang === 'hi' ? '🌌 आज का आकाश दर्शन (वैदिक गोचर)' : "🌌 TODAY'S SKY (VEDIC TRANSITS)"}</span>
                </div>
                <p className="text-sm sm:text-base text-text-main leading-relaxed">{transitSummary}</p>
              </div>
            </div>
          )}

          {/* Daily Parashari Snapshot — Moon Nakshatra + Pada + Yuti + Drishti + Vedha */}
          {parashariSummary && parashariSummary.moon_nakshatra && (
            <div className="max-w-4xl mx-auto mb-6 px-4" data-testid="parashari-snapshot">
              <div className="bg-gradient-to-br from-indigo-900/30 via-purple-900/20 to-gold/10 border border-indigo-400/30 rounded-2xl p-4 sm:p-5 backdrop-blur-sm text-left">
                <div className="flex items-center gap-2 text-indigo-300 text-xs sm:text-sm font-semibold mb-3">
                  <Moon className="w-4 h-4" />
                  <span className="tracking-widest uppercase">
                    {lang === 'hi' ? '🌙 आज का सूक्ष्म पराशरीय सार' : '🌙 Today\'s Parashari Snapshot'}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-indigo-300/70 mb-1">{lang === 'hi' ? 'चंद्र राशि' : 'Moon Rashi'}</div>
                    <div className="text-text-main font-medium">{parashariSummary.moon_sign_hi}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-indigo-300/70 mb-1">{lang === 'hi' ? 'चंद्र नक्षत्र' : 'Nakshatra'}</div>
                    <div className="text-text-main font-medium">{parashariSummary.moon_nakshatra}</div>
                    <div className="text-[11px] text-gold/80 mt-0.5">{lang === 'hi' ? 'स्वामी:' : 'Lord:'} {parashariSummary.moon_nakshatra_lord}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-indigo-300/70 mb-1">{lang === 'hi' ? 'चरण/पाद' : 'Pada'}</div>
                    <div className="text-text-main font-medium">{parashariSummary.moon_pada}</div>
                    <div className="text-[11px] text-gold/80 mt-0.5">{lang === 'hi' ? 'स्वामी:' : 'Lord:'} {parashariSummary.moon_pada_lord}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-indigo-300/70 mb-1">{lang === 'hi' ? 'वार' : 'Day'}</div>
                    <div className="text-text-main font-medium">{parashariSummary.vaar}</div>
                    <div className="text-[11px] text-gold/80 mt-0.5">{lang === 'hi' ? 'स्वामी:' : 'Lord:'} {parashariSummary.vaar_lord}</div>
                  </div>
                </div>
                {(parashariSummary.yuti_with_moon?.length > 0 || parashariSummary.drishti_on_moon?.length > 0 || parashariSummary.vedha_pairs?.length > 0) && (
                  <div className="mt-4 pt-3 border-t border-indigo-400/15 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs sm:text-sm">
                    {parashariSummary.yuti_with_moon?.length > 0 && (
                      <div data-testid="moon-yuti">
                        <span className="text-pink-300/90 font-semibold">{lang === 'hi' ? 'चंद्र-युति: ' : 'Moon Yuti: '}</span>
                        <span className="text-text-main">{parashariSummary.yuti_with_moon.join(', ')}</span>
                      </div>
                    )}
                    {parashariSummary.drishti_on_moon?.length > 0 && (
                      <div data-testid="moon-drishti">
                        <span className="text-cyan-300/90 font-semibold">{lang === 'hi' ? 'चंद्र पर दृष्टि: ' : 'Aspect on Moon: '}</span>
                        <span className="text-text-main">{parashariSummary.drishti_on_moon.join(', ')}</span>
                      </div>
                    )}
                    {parashariSummary.vedha_pairs?.length > 0 && (
                      <div data-testid="vedha-pairs">
                        <span className="text-red-300/90 font-semibold">{lang === 'hi' ? 'वेध सम्बन्ध: ' : 'Vedha: '}</span>
                        <span className="text-text-main">{parashariSummary.vedha_pairs.join(' · ')}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sun/Moon Toggle */}
          <div className="flex items-center justify-center gap-3 mb-4" data-testid="sign-mode-toggle">
            <button onClick={() => setMode('sun')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${mode === 'sun' ? 'bg-gold/20 text-gold border border-gold/40' : 'bg-white/5 text-text-muted border border-white/10 hover:border-gold/20'}`}
              data-testid="sun-sign-btn">
              <Sun className="w-4 h-4" /> {lang === 'hi' ? 'सूर्य राशि (DOB)' : 'Sun Sign (DOB)'}
            </button>
            <button onClick={() => setMode('moon')}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${mode === 'moon' ? 'bg-primary/20 text-primary border border-primary/40' : 'bg-white/5 text-text-muted border border-white/10 hover:border-primary/20'}`}
              data-testid="moon-sign-btn">
              <Moon className="w-4 h-4" /> {lang === 'hi' ? 'चंद्र राशि' : 'Moon Sign'}
            </button>
          </div>

          {/* DOB Picker for Sun Sign */}
          {mode === 'sun' && (
            <div className="max-w-xs mx-auto mb-4" data-testid="dob-picker">
              <label className="block text-xs text-text-muted mb-1">
                {lang === 'hi' ? 'अपनी जन्मतिथि डालें — राशि अपने आप पता चल जाएगी' : 'Enter your Date of Birth to find your sign'}
              </label>
              <input type="date" value={dob} onChange={(e) => handleDobChange(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-text-main text-center"
                data-testid="dob-input" />
            </div>
          )}
          {mode === 'moon' && (
            <p className="text-xs text-text-muted max-w-md mx-auto mb-4">
              {lang === 'hi' ? 'चंद्र राशि कुंडली से पता चलती है। अगर आपको अपनी चंद्र राशि पता है तो नीचे से चुनें।' : 'Moon sign is determined from your birth chart. Select yours below if you know it.'}
            </p>
          )}
          <div className="divine-divider mt-4 max-w-xs mx-auto" />
        </div>
      </section>

      {/* Today's Featured Rashi banner */}
      {!loading && rashifal.length > 0 && (() => {
        const feat = rashifal.find(r => r.is_featured);
        if (!feat) return null;
        const featIdx = rashifal.indexOf(feat);
        return (
          <section className="container-custom -mt-2 mb-6">
            <div
              data-testid="featured-rashi-banner"
              onClick={() => { setSelected(featIdx); trackRashifalView((feat.name_en || feat.name) + '_featured'); setTimeout(() => document.getElementById('rashifal-detail')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80); }}
              className="cursor-pointer relative overflow-hidden rounded-2xl border border-orange-500/40 bg-gradient-to-r from-orange-500/15 via-gold/15 to-rose-500/15 p-5 sm:p-6 hover:border-orange-400/70 transition-all">
              <div className="absolute top-0 right-0 px-3 py-1 bg-orange-500 text-xs font-bold text-white rounded-bl-2xl flex items-center gap-1">
                <Flame className="w-3 h-3" /> {lang === 'hi' ? 'आज की विशेष राशि' : "TODAY'S FEATURED"}
              </div>
              <div className="flex items-center gap-4 mt-2">
                <span className="text-5xl sm:text-6xl">{feat.symbol}</span>
                <div className="flex-1">
                  <h3 className="font-heading text-2xl sm:text-3xl text-text-main">{feat.name} ({feat.name_en})</h3>
                  <p className="text-sm text-orange-300/90 mt-1">
                    {lang === 'hi' ? 'आज ग्रह योग इस राशि के लिए विशेष प्रभावी हैं — विस्तृत फलादेश पढ़ें।' : 'Planetary alignments are specially impacting this rashi today — read the full detail.'}
                  </p>
                </div>
                <span className="hidden sm:flex items-center text-orange-300 text-sm font-semibold">
                  {lang === 'hi' ? 'पढ़ें →' : 'Read →'}
                </span>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Rashi Grid */}
      <section className="section-padding pt-2">
        <div className="container-custom">
          {loading ? (
            <div className="text-center text-text-muted py-12" data-testid="rashifal-loading">{lang === 'hi' ? 'राशिफल तैयार हो रहा है... आचार्य गणनाएं कर रहे हैं' : 'Loading horoscope... Acharya is calculating'}</div>
          ) : rashifal.length === 0 ? (
            <div className="text-center text-text-muted py-12">{lang === 'hi' ? 'राशिफल उपलब्ध नहीं है' : 'Horoscope not available'}</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="rashifal-grid">
              {rashifal.map((rashi, idx) => (
                <button key={idx} onClick={() => { setSelected(idx); trackRashifalView(rashi.name_en || rashi.name); setTimeout(() => document.getElementById('rashifal-detail')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80); }}
                  className={`relative glass-card p-5 text-center hover:border-gold/40 transition-all cursor-pointer ${selected === idx ? 'border-gold/60 ring-2 ring-gold/20' : ''} ${rashi.is_critical ? 'border-red-500/40' : ''} ${rashi.is_featured ? 'border-orange-400/60' : ''}`}
                  data-testid={`rashi-card-${rashi.name_en?.toLowerCase()}`}>
                  {rashi.is_critical && (
                    <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/90 text-white text-[10px] font-bold animate-pulse" data-testid="critical-badge">
                      <AlertTriangle className="w-3 h-3" /> {lang === 'hi' ? 'विशेष' : 'ALERT'}
                    </span>
                  )}
                  {rashi.is_featured && !rashi.is_critical && (
                    <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500/90 text-white text-[10px] font-bold">
                      <Flame className="w-3 h-3" /> {lang === 'hi' ? 'खास' : 'HOT'}
                    </span>
                  )}
                  <div className="text-4xl mb-2">{rashi.symbol}</div>
                  <h3 className="font-heading text-lg text-text-main">{rashi.name}</h3>
                  <p className="text-xs text-text-muted">{rashi.name_en}</p>
                  {mode === 'sun' && rashi.sun_dates_hi && (
                    <p className="text-[9px] text-gold/50 mt-1" title={lang === 'hi' ? 'सूर्य राशि अवधि' : 'Sun-sign range'}>
                      {lang === 'hi' ? `(${rashi.sun_dates_hi})` : `(${rashi.sun_dates})`}
                    </p>
                  )}
                  <div className="flex justify-center gap-0.5 mt-2">{ratingStars(rashi.rating)}</div>
                </button>
              ))}
            </div>
          )}

          {/* Selected Rashi Detail */}
          {sel && (
            <div id="rashifal-detail" className="mt-10 glass-card p-6 sm:p-8 max-w-3xl mx-auto" data-testid="rashifal-detail">
              <div className="flex items-center gap-4 mb-6">
                <span className="text-5xl">{sel.symbol}</span>
                <div className="flex-1">
                  <h2 className="font-heading text-2xl sm:text-3xl text-text-main">{sel.name} ({sel.name_en})</h2>
                  <div className="flex items-center gap-3 mt-1">
                    <div className="flex gap-1">{ratingStars(sel.rating)}</div>
                    {sel.is_critical && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/30 text-xs font-semibold">
                        <AlertTriangle className="w-3 h-3" /> {lang === 'hi' ? 'विशेष ग्रह योग' : 'Critical Transit'}
                      </span>
                    )}
                  </div>
                  {mode === 'sun' && sel.sun_dates && (
                    <p className="text-[10px] text-text-muted/60 mt-1">
                      {lang === 'hi' ? `सूर्य राशि अवधि: ${sel.sun_dates_hi}` : `Sun-sign range: ${sel.sun_dates}`}
                    </p>
                  )}
                </div>
              </div>

              {/* Detailed Parashari Prediction */}
              <div className="mb-6">
                <h3 className="text-sm uppercase tracking-widest text-gold mb-1 font-semibold flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5" />
                  {lang === 'hi' ? 'आज का विस्तृत फलादेश' : "Today's Detailed Forecast"}
                </h3>
                <p className="text-xs text-text-muted/80 mb-3 ml-5" data-testid="prediction-date">
                  {dateHi || formatDate(date)}
                </p>

                {/* Moon house from this rashi + Chandrashtama / 6/12 badge */}
                {sel.moon_house_from_rashi && (
                  <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-500/15 border border-indigo-400/30 text-indigo-200" data-testid="moon-house-badge">
                      <Moon className="w-3 h-3" />
                      {lang === 'hi' ? `चंद्र भाव: ${sel.moon_house_from_rashi}` : `Moon in House: ${sel.moon_house_from_rashi}`}
                    </span>
                    {sel.is_chandrashtama && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-500/20 border border-red-400/40 text-red-200 font-semibold" data-testid="chandrashtama-badge">
                        🔴 {lang === 'hi' ? 'चंद्राष्टम — आज विशेष सावधानी' : 'Chandrashtama — Caution Day'}
                      </span>
                    )}
                    {sel.is_negative_moon && !sel.is_chandrashtama && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-200" data-testid="negative-moon-badge">
                        ⚠️ {sel.moon_house_from_rashi === 6
                          ? (lang === 'hi' ? 'षष्ठ चंद्र' : '6th-house Moon')
                          : (lang === 'hi' ? 'द्वादश चंद्र' : '12th-house Moon')}
                      </span>
                    )}
                  </div>
                )}

                <p className="text-text-main text-base sm:text-lg leading-[1.9] whitespace-pre-line" data-testid="rashifal-prediction">{sel.prediction}</p>
              </div>

              {/* Motivational Shloka — shown ONLY for difficult Moon transits (6/8/12) */}
              {sel.motivational_shloka && (
                <div className="mb-6 bg-gradient-to-br from-rose-900/25 to-purple-900/15 border-l-4 border-rose-400 rounded-r-xl p-5" data-testid="motivational-shloka">
                  <div className="text-xs uppercase tracking-widest text-rose-300 mb-2 font-semibold flex items-center gap-2">
                    🪷 {lang === 'hi' ? 'आज के लिए प्रेरणादायक श्लोक' : 'Motivational Shloka for Today'}
                  </div>
                  <p className="text-rose-100 font-heading text-base sm:text-lg leading-relaxed mb-2 italic">{sel.motivational_shloka}</p>
                  {sel.motivational_meaning && (
                    <p className="text-sm text-text-muted leading-relaxed">— {sel.motivational_meaning}</p>
                  )}
                  {sel.motivational_source && (
                    <p className="text-xs text-rose-300/80 mt-2 font-medium">📖 {sel.motivational_source}</p>
                  )}
                </div>
              )}

              {/* Sanskrit Shloka card */}
              {sel.shloka && (
                <div className="mb-6 bg-gradient-to-br from-amber-900/20 to-orange-900/10 border-l-4 border-gold rounded-r-xl p-5" data-testid="rashifal-shloka">
                  <div className="text-xs uppercase tracking-widest text-gold mb-2 font-semibold flex items-center gap-2">
                    🕉️ {lang === 'hi' ? 'पराशरीय श्लोक' : 'Parashari Shloka'}
                  </div>
                  <p className="text-gold font-heading text-base sm:text-lg leading-relaxed mb-2 italic">{sel.shloka}</p>
                  {sel.shloka_meaning && (
                    <p className="text-sm text-text-muted leading-relaxed">— {sel.shloka_meaning}</p>
                  )}
                  {sel.shloka_source && (
                    <p className="text-xs text-gold/70 mt-2 font-medium">📿 {sel.shloka_source}</p>
                  )}
                </div>
              )}

              {/* Three pillars: Love / Career / Health */}
              <div className="grid sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-pink-500/10 border border-pink-500/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-pink-400 mb-2 font-semibold"><Heart className="w-4 h-4" /> {lang === 'hi' ? 'प्रेम व संबंध' : 'Love & Relationship'}</div>
                  <p className="text-sm text-text-muted leading-relaxed">{sel.love}</p>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-blue-400 mb-2 font-semibold"><Briefcase className="w-4 h-4" /> {lang === 'hi' ? 'करियर व धन' : 'Career & Wealth'}</div>
                  <p className="text-sm text-text-muted leading-relaxed">{sel.career}</p>
                </div>
                <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-green-400 mb-2 font-semibold"><Activity className="w-4 h-4" /> {lang === 'hi' ? 'स्वास्थ्य' : 'Health'}</div>
                  <p className="text-sm text-text-muted leading-relaxed">{sel.health}</p>
                </div>
              </div>

              {/* Savdhani */}
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                {sel.savdhani && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4" data-testid="rashifal-savdhani">
                    <div className="flex items-center gap-2 text-amber-400 mb-2 font-semibold"><ShieldAlert className="w-4 h-4" /> {lang === 'hi' ? 'आज की सावधानी' : 'Today\'s Caution'}</div>
                    <p className="text-sm text-text-main">{sel.savdhani}</p>
                  </div>
                )}
              </div>

              {/* Vaidik Upay section — DISABLED per Guruji's request (Feb 2026).
                  Engine code still computes on backend but UI is hidden until
                  Guruji approves a cleaner re-design. */}
              {false && sel.vaidik_upay && sel.vaidik_upay.beej_mantra && (
                <div className="mb-6">
                  <VaidikUpayCard upay={sel.vaidik_upay} lang={lang} />
                </div>
              )}

              {/* Legacy "आज का घरेलू उपाय" — REMOVED per Guruji's Feb 2026 spec rule #8 */}

              <div className="flex flex-wrap gap-x-6 gap-y-2 mb-6 text-sm text-text-muted">
                <span>{lang === 'hi' ? 'शुभ रंग' : 'Lucky Color'}: <span className="text-gold font-semibold">{sel.lucky_color}</span></span>
                <span>{lang === 'hi' ? 'शुभ अंक' : 'Lucky Number'}: <span className="text-gold font-semibold">{sel.lucky_number}</span></span>
              </div>

              {/* Personal Consultation Soft Hook + CTA */}
              <div className="bg-gradient-to-br from-purple-900/40 via-rose-900/20 to-amber-900/30 border-2 border-gold/40 rounded-2xl p-5 sm:p-6" data-testid="consultation-hook">
                <p className="text-text-main text-sm sm:text-base leading-relaxed mb-4">
                  ⚠️ <span className="text-gold font-semibold">{lang === 'hi' ? `${sel.name} राशि वालों के लिए विशेष सूचना:` : `Special Note for ${sel.name_en}:`}</span> {sel.consultation_hook}
                </p>
                <a
                  href={buildWhatsAppLink(sel, sel.is_critical)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => { trackConsultationCTAClick('rashi_detail', { rashi: sel.name_en || sel.name, is_critical: !!sel.is_critical }); trackWhatsAppClick('rashi_detail'); }}
                  className="btn-gold inline-flex items-center gap-2 w-full sm:w-auto justify-center"
                  data-testid="consult-whatsapp-cta">
                  <MessageCircle className="w-4 h-4" />
                  {lang === 'hi' ? 'आचार्य से Personal Consultation Book करें' : 'Book Personal Consultation with Acharya'}
                </a>
              </div>
            </div>
          )}

          {/* Mid-page Trust + Authority Block (after rashi grid) */}
          <div className="max-w-4xl mx-auto mt-12" data-testid="trust-authority-block">
            <div className="relative overflow-hidden rounded-3xl border border-gold/40 bg-gradient-to-br from-purple-950/60 via-amber-950/30 to-purple-950/60 p-6 sm:p-10 backdrop-blur-sm">
              <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-gold/10 rounded-full blur-3xl" />
              <div className="relative grid sm:grid-cols-2 gap-6 items-center">
                <div>
                  <div className="text-xs tracking-[0.3em] uppercase text-gold font-bold mb-3">🕉️ {lang === 'hi' ? 'पराशर शास्त्र विशेषज्ञ' : 'Parashari Shastra Expert'}</div>
                  <h3 className="font-heading text-2xl sm:text-3xl text-text-main mb-3 leading-tight">
                    {lang === 'hi' ? 'आचार्य Vivek Mudgal' : 'Acharya Vivek Mudgal'}
                  </h3>
                  <p className="text-text-muted text-sm sm:text-base leading-relaxed mb-4">
                    {lang === 'hi'
                      ? '33+ वर्षों का गहन वैदिक अनुभव • 33,000+ संतुष्ट परामर्श • Maharshi Parashar सिद्धांतों पर आधारित प्रामाणिक ज्योतिष विश्लेषण'
                      : '33+ years of deep Vedic experience • 33,000+ satisfied consultations • Authentic astrology rooted in Maharshi Parashar principles'}
                  </p>
                  <div className="bg-gold/10 border border-gold/30 rounded-lg p-3 mb-4">
                    <p className="text-sm text-text-main italic leading-relaxed">
                      "{lang === 'hi' ? 'Guruji ने मेरी शादी की 7 साल पुरानी समस्या एक ही परामर्श में solve कर दी। मांगलिक दोष का सटीक उपाय बताया।' : 'Guruji solved my 7-year-old marriage problem in just one consultation. Gave precise Manglik dosha remedy.'}"
                    </p>
                    <p className="text-xs text-gold mt-2">— Priya Sharma, {lang === 'hi' ? 'दिल्ली' : 'Delhi'}</p>
                  </div>
                </div>
                <div className="text-center sm:text-right">
                  <div className="inline-block bg-gradient-to-br from-gold/20 to-orange-500/10 border border-gold/40 rounded-2xl p-5 mb-4">
                    <div className="text-xs uppercase tracking-widest text-gold font-bold mb-1">{lang === 'hi' ? 'Premium Personal Consultation' : 'Premium Personal Consultation'}</div>
                    <div className="text-3xl sm:text-4xl font-heading text-gold mb-1">₹5,100</div>
                    <div className="text-xs text-text-muted">{lang === 'hi' ? 'विस्तृत Parashari कुंडली विश्लेषण' : 'Detailed Parashari Kundli Analysis'}</div>
                  </div>
                  <p className="text-xs text-amber-300 mb-4 font-semibold animate-pulse">
                    🪔 {lang === 'hi' ? 'आज केवल 3 personal consultation slots उपलब्ध हैं' : 'Only 3 personal consultation slots available today'}
                  </p>
                  <a
                    href={`https://wa.me/919311369729?text=${encodeURIComponent(lang === 'hi' ? 'नमस्कार आचार्य Vivek जी 🙏\n\nमुझे अपनी जन्म कुंडली का गहन Parashari विश्लेषण व व्यक्तिगत परामर्श करवाना है।\n\nकृपया available timing व booking details बताएं।' : 'Namaste Acharya Vivek ji 🙏\n\nI want to book a detailed Parashari analysis & personal consultation for my birth chart.\n\nPlease share available timings and booking details.')}`}
                    target="_blank" rel="noopener noreferrer"
                    onClick={() => { trackConsultationCTAClick('trust_block'); trackWhatsAppClick('trust_block'); }}
                    className="btn-gold inline-flex items-center gap-2"
                    data-testid="trust-block-cta">
                    <MessageCircle className="w-4 h-4" />
                    {lang === 'hi' ? 'अभी Book करें' : 'Book Now'}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Floating Booking CTA */}
      <a
        href={`https://wa.me/919311369729?text=${encodeURIComponent(lang === 'hi' ? 'नमस्कार आचार्य Vivek जी 🙏\n\nमुझे Personal Consultation Book करवानी है।' : 'Namaste Acharya Vivek ji 🙏\n\nI want to book a Personal Consultation.')}`}
        target="_blank" rel="noopener noreferrer"
        onClick={() => { trackConsultationCTAClick('sticky_floating'); trackWhatsAppClick('sticky_floating'); }}
        className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 z-50 group"
        data-testid="sticky-consult-cta"
      >
        <div className="relative">
          <span className="absolute inset-0 rounded-full bg-gold/40 animate-ping" />
          <span className="relative flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-gold to-amber-500 text-purple-950 font-bold text-sm shadow-2xl border-2 border-white/20 hover:scale-105 transition-transform">
            <MessageCircle className="w-4 h-4" />
            <span className="hidden sm:inline">{lang === 'hi' ? 'Personal Consultation' : 'Book Consultation'}</span>
            <span className="sm:hidden">{lang === 'hi' ? 'Book करें' : 'Book'}</span>
            <span className="hidden md:inline-block px-2 py-0.5 rounded-full bg-purple-950 text-gold text-[10px] font-bold ml-1">₹5,100</span>
          </span>
        </div>
      </a>

      <RelatedTools />

      {/* Daily Rashifal Subscription */}
      <section className="section-padding pt-0 pb-12" data-testid="rashifal-subscribe-section">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <RashifalSubscribe defaultRashiIdx={selected} lang={lang} />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
